import { POOL_COLORS as SYSTEM_POOL_COLORS } from '../pages/eval-relations/poolColDefs';

/** Ant Design Tag preset → hex（用于非 Tag 装饰，如左边框） */
export const TALENT_POOL_TAG_HEX: Record<string, string> = {
  default: '#d9d9d9',
  blue: '#1677ff',
  cyan: '#13c2c2',
  green: '#52c41a',
  orange: '#fa8c16',
  purple: '#722ed1',
  red: '#ff4d4f',
  volcano: '#fa541c',
  gold: '#faad14',
  lime: '#a0d911',
  geekblue: '#2f54eb',
  magenta: '#eb2f96',
};

/** 自定义池 / 分组着色 palette（排除系统池已占用的 preset） */
const CUSTOM_PALETTE = [
  'purple',
  'geekblue',
  'magenta',
  'volcano',
  'orange',
  'lime',
  'green',
  'cyan',
  'blue',
  'gold',
  'red',
] as const;

const SYSTEM_USED = new Set(Object.values(SYSTEM_POOL_COLORS));
const ASSIGNABLE_PALETTE = CUSTOM_PALETTE.filter((c) => !SYSTEM_USED.has(c));

export interface TalentPoolColorInput {
  code: string;
  relationGroup?: string | null;
}

function stableHash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export interface TalentPoolColorMaps {
  colorByCode: Record<string, string>;
  colorByRelationGroup: Record<string, string>;
}

/** 系统池固定色；有 relationGroup 的同组同色；无组池按 code 稳定映射 */
export function buildTalentPoolColorMaps(pools: TalentPoolColorInput[]): TalentPoolColorMaps {
  const colorByCode: Record<string, string> = { ...SYSTEM_POOL_COLORS };
  const colorByRelationGroup: Record<string, string> = {};

  const groups = [
    ...new Set(
      pools
        .map((p) => p.relationGroup?.trim())
        .filter((g): g is string => !!g),
    ),
  ].sort((a, b) => a.localeCompare(b, 'zh-CN'));

  let paletteIdx = 0;
  const nextPaletteColor = () =>
    ASSIGNABLE_PALETTE[paletteIdx++ % ASSIGNABLE_PALETTE.length] ?? 'default';

  for (const group of groups) {
    colorByRelationGroup[group] = nextPaletteColor();
  }

  for (const pool of pools) {
    if (colorByCode[pool.code]) continue;

    const group = pool.relationGroup?.trim();
    if (group && colorByRelationGroup[group]) {
      colorByCode[pool.code] = colorByRelationGroup[group];
    } else {
      const hash = stableHash(pool.code);
      colorByCode[pool.code] = ASSIGNABLE_PALETTE[hash % ASSIGNABLE_PALETTE.length] ?? 'default';
    }
  }

  return { colorByCode, colorByRelationGroup };
}

export function resolveTalentPoolColor(
  code: string,
  colorByCode: Record<string, string>,
): string {
  if (colorByCode[code]) return colorByCode[code];
  const hash = stableHash(code);
  return ASSIGNABLE_PALETTE[hash % ASSIGNABLE_PALETTE.length] ?? 'default';
}

export function getTalentPoolTagHex(color: string | undefined): string {
  if (!color) return TALENT_POOL_TAG_HEX.default;
  return TALENT_POOL_TAG_HEX[color] ?? TALENT_POOL_TAG_HEX.default;
}
